import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./Style.css";
import MenuBar from "./MenuBar";
import Footer from "./Footer";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import PageTabs from "./shared/PageTabs";
import CollapsiblePanel from "./shared/CollapsiblePanel";

const envVariable = require("../EnvironmentVariables.json");

const ITEMS_PER_PAGE = 10;
const FALLBACK_LEAVES = [];

const initialLeaveState = {
  selectedDate: "",
  startTime: "00:00",
  endTime: "00:00",
  selectedMechanic: "",
  reason: "sick",
  title: "",
  notes: "",
};

const LeaveTabs = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
};

const ROADMAP_TABS = [
  { id: "insights", label: "Insights", enabled: false, comingSoon: true },
  { id: "automation", label: "Automation", enabled: false, comingSoon: true },
  { id: "integrations", label: "Integrations", enabled: false, comingSoon: true },
];

const LeaveRequest = () => {
  const [leaves, setLeaves] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(LeaveTabs.PENDING);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mechanics, setMechanics] = useState([]);
  const [createForm, setCreateForm] = useState(initialLeaveState);
  const [feedback, setFeedback] = useState(null);
  const [timeErr, setTimeErr] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [taskConflictDialog, setTaskConflictDialog] = useState(null);
  const pageTabs = useMemo(
    () => [
      { id: LeaveTabs.PENDING, label: "Pending Requests", enabled: true },
      { id: LeaveTabs.APPROVED, label: "Approved Requests", enabled: true },
      { id: LeaveTabs.REJECTED, label: "Rejected Requests", enabled: true },
      ...ROADMAP_TABS,
    ],
    []
  );

  const extractTimeFromDateTime = useCallback((value = "") => {
    if (!value) {
      return "—";
    }

    try {
      // Parse UTC datetime and convert to local time
      // Backend stores times in UTC, we need to display in local timezone
      const s = value.includes('T') ? value : value.replace(' ', 'T');
      const withZ = s.endsWith('Z') ? s : s + 'Z';
      const utcDate = new Date(withZ);
      if (isNaN(utcDate.getTime())) {
        // Fallback to raw string extraction if parsing fails
        const isoSafe = value.replace("T", " ");
        const [, timePart] = isoSafe.split(" ");
        return timePart && timePart.length >= 5 ? timePart.substring(0, 5) : "—";
      }

      // Get local time in HH:MM format
      const hours = String(utcDate.getHours()).padStart(2, '0');
      const minutes = String(utcDate.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      console.error('[LeaveRequest] Error extracting time:', error, value);
      return "—";
    }
  }, []);

  const extractDateValue = useCallback((value = "") => {
    if (!value) {
      return "—";
    }

    try {
      // If it's already a plain date (YYYY-MM-DD), return as-is without timezone conversion
      // The 'date' column in the database is a DATE type (calendar date), not a datetime
      if (value.length === 10 && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return value;
      }

      // If it's a datetime string, extract the date portion from start_date_time
      // Parse UTC datetime and convert to local date
      const s = value.includes('T') ? value : value.replace(' ', 'T');
      const withZ = s.endsWith('Z') ? s : s + 'Z';
      const utcDate = new Date(withZ);
      if (isNaN(utcDate.getTime())) {
        // Fallback to raw date extraction if parsing fails
        const safeValue = value.replace("T", " ");
        const [datePart] = safeValue.split(" ");
        return datePart && datePart.includes("-") ? datePart : value;
      }

      // Get local date in YYYY-MM-DD format (converted from UTC datetime)
      const year = utcDate.getFullYear();
      const month = String(utcDate.getMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('[LeaveRequest] Error extracting date:', error, value);
      return "—";
    }
  }, []);

  const fetchMechanics = useCallback(async () => {
    const scId = localStorage.getItem("SC_Id");
    if (!scId) {
      return;
    }

    try {
      const response = await fetch(`${envVariable.serverId}/SCO/GetMechanics.php?SC_Id=${scId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch mechanics");
      }

      const data = await response.json();
      setMechanics(data.mechanics || []);
    } catch (mechanicError) {
      console.error("Error fetching mechanics:", mechanicError);
    }
  }, []);

  const fetchLeaves = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const apiKey =
        localStorage.getItem("api_key") ||
        localStorage.getItem("@api_key") ||
        "";
      // const mechanicId = localStorage.getItem("User_Id") || "";
      const scId = localStorage.getItem("SC_Id") || "";

      if (!apiKey || !scId) {
        throw new Error("Missing shop context. Please log in again.");
      }

      const response = await fetch(`${envVariable.serverId}/SCO/getLeaves.php`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          // "X-API-KEY": apiKey,
        },
        body: JSON.stringify({
          // mechanic_id: mechanicId,
          sc_id: scId,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch leaves");
      }

      const payload = await response.json();
      const results = Array.isArray(payload?.results)
        ? payload.results
        : Array.isArray(payload?.leaves)
        ? payload.leaves
        : Array.isArray(payload)
        ? payload
        : [];

      console.log("Raw leaves data:", results);

      const normalized =
        results.length > 0
          ? results.map((item, index) => {
              const startDateTime =
                item.start_date_time ||
                item.startDateTime ||
                item.startdatetime ||
                "";
              const endDateTime =
                item.end_date_time ||
                item.endDateTime ||
                item.enddatetime ||
                "";

              const uiLeaveDate = extractDateValue(
                item.leave_date || item.date || startDateTime
              );
              const uiStartTime =
                item.start_time || extractTimeFromDateTime(startDateTime);
              const uiEndTime =
                item.end_time || extractTimeFromDateTime(endDateTime);

              const dbDate =
                item.date ||
                item.leave_date ||
                (startDateTime ? startDateTime.split(" ")[0] : uiLeaveDate);
              const buildDateTimeValue = (dateValue, timeValue, fallback) => {
                if (fallback) {
                  return fallback;
                }
                if (!dateValue || dateValue === "—") {
                  return "";
                }
                if (!timeValue || timeValue === "—") {
                  return `${dateValue} 00:00:00`;
                }
                return timeValue.length === 5
                  ? `${dateValue} ${timeValue}:00`
                  : `${dateValue} ${timeValue}`;
              };

              const normalizedStartDateTime = buildDateTimeValue(
                dbDate,
                uiStartTime,
                startDateTime
              );
              const normalizedEndDateTime = buildDateTimeValue(
                dbDate,
                uiEndTime,
                endDateTime
              );

              const rawPayload = {
                leave_id: item.leave_id ?? item.id ?? item.slot_id ?? index,
                mechanic_id:
                  item.mechanic_id ??
                  item.mechanicId ??
                  item.MechanicId ??
                  "",
                sc_id: item.sc_id ?? item.scId ?? scId,
                date: dbDate,
                start_date_time: normalizedStartDateTime,
                end_date_time: normalizedEndDateTime,
                start_time: uiStartTime && uiStartTime !== "—" ? uiStartTime : "",
                end_time: uiEndTime && uiEndTime !== "—" ? uiEndTime : "",
                mech_status: item.mech_status ?? item.mechStatus ?? "",
                slot_id: item.slot_id ?? item.slotId ?? null,
                task_id: item.task_id ?? item.taskId ?? null,
                leave_reason:
                  item.leave_reason ?? item.leaveReason ?? item.reason ?? "",
                title: item.title ?? item.leave_title ?? "Leave",
                status: item.status ?? "pending",
                notes: item.notes ?? "",
              };

              return {
                id: String(rawPayload.leave_id),
                leaveId: rawPayload.leave_id,
                mechanicName:
                  item.first_name ||
                  item.mechanic_name ||
                  item.Mechanic ||
                  "Unknown",
                title: rawPayload.title,
                leaveDate: uiLeaveDate,
                startTime: uiStartTime,
                endTime: uiEndTime,
                status: String(rawPayload.status ?? "pending").toLowerCase(),
                raw: rawPayload,
              };
            })
          : FALLBACK_LEAVES;
      console.log("Fetched leaves:", normalized);
      setLeaves(normalized);
    } catch (fetchError) {
      console.error("Error fetching leaves:", fetchError);
      setError(fetchError?.message || "Unable to load leaves");
      setLeaves(FALLBACK_LEAVES);
    } finally {
      setIsLoading(false);
    }
  }, [extractDateValue, extractTimeFromDateTime]);

  const filteredLeaves = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return leaves
      .filter((leave) => leave.status === statusFilter)
      .filter((leave) =>
        term ? leave.mechanicName?.toLowerCase().includes(term) : true
      );
  }, [leaves, searchTerm, statusFilter]);

  const sortedLeaves = useMemo(() => {
    if (!sortField || !sortOrder) {
      return [...filteredLeaves];
    }
    const valueFor = (leave) => {
      switch (sortField) {
        case "mechanicName":
          return leave.mechanicName?.toLowerCase() || "";
        case "leaveDate": {
          const timestamp = new Date(leave.leaveDate).getTime();
          return Number.isNaN(timestamp) ? 0 : timestamp;
        }
        case "leaveId":
        default:
          return Number(leave.leaveId) || 0;
      }
    };
    const modifier = sortOrder === "asc" ? 1 : -1;
    return [...filteredLeaves].sort((a, b) => {
      const valueA = valueFor(a);
      const valueB = valueFor(b);
      if (valueA < valueB) return -1 * modifier;
      if (valueA > valueB) return 1 * modifier;
      return 0;
    });
  }, [filteredLeaves, sortField, sortOrder]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(sortedLeaves.length / ITEMS_PER_PAGE));
  }, [sortedLeaves.length]);

  const visibleLeaves = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedLeaves.slice(start, start + ITEMS_PER_PAGE);
  }, [currentPage, sortedLeaves]);

  useEffect(() => {
    fetchLeaves();
    fetchMechanics();
  }, [fetchLeaves, fetchMechanics]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleSortFieldChange = (field) => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else if (sortOrder === "desc") {
        setSortField(null);
        setSortOrder(null);
      } else {
        setSortOrder("asc");
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handlePaginationLeft = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handlePaginationRight = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const formatStatus = (status) => {
    if (!status) {
      return "Pending";
    }

    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const updateLeaveStatus = async (leave, nextStatus, confirmUnassignment = false) => {
    if (!leave || leave.status === nextStatus) {
      return;
    }

    const contextKey = `${leave.id}-${nextStatus}`;
    setActionInProgress(contextKey);
    setError("");

    try {
      const apiKey =
        localStorage.getItem("api_key") ||
        localStorage.getItem("@api_key") ||
        "";
      const scId = localStorage.getItem("SC_Id") || "";

      if (!apiKey || !scId) {
        throw new Error("Missing shop context. Please log in again.");
      }
      console.log("Updating leave status:", {
        leave: leave,
        scId,
        nextStatus,
        confirmUnassignment,
      });

      const response = await fetch(
        `${envVariable.serverId}/SCO/VacationRequest.php`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: "update",
            sc_id: scId,
            slot_id: leave.raw.slot_id,
            date: leave.raw.date,
            start_date_time: leave.raw.start_date_time,
            end_date_time: leave.raw.end_date_time,
            leave_reason: leave.raw.leave_reason,
            title: leave.raw.title,
            notes: leave.raw.notes,
            status: nextStatus,
            confirm_unassignment: confirmUnassignment,
          }),
        }
      );

      const result = await response.json();

      // Check if backend is requesting confirmation for task conflicts
      if (result.requires_unassignment && result.conflicting_tasks) {
        console.log('[LeaveRequest] Task conflicts detected:', result.conflicting_tasks);
        setTaskConflictDialog({
          leave,
          nextStatus,
          conflictingTasks: result.conflicting_tasks,
        });
        setActionInProgress(null);
        return;
      }

      if (!response.ok || result.status === 'Error') {
        throw new Error(result.message || "Failed to update leave status");
      }

      setLeaves((prev) =>
        prev.map((item) =>
          item.id === leave.id
            ? { ...item, status: nextStatus, raw: { ...item.raw, status: nextStatus } }
            : item
        )
      );
    } catch (statusError) {
      console.error("Error updating leave status:", statusError);
      setError(statusError?.message || "Unable to update leave");
    } finally {
      setActionInProgress(null);
    }
  };

  const handleActionRequest = (leave, nextStatus) => {
    if (!leave || leave.status === nextStatus) {
      return;
    }
    setConfirmAction({ leave, nextStatus });
  };

  const handleConfirmAction = (confirmed) => {
    if (!confirmAction) {
      return;
    }
    if (confirmed) {
      updateLeaveStatus(confirmAction.leave, confirmAction.nextStatus);
    }
    setConfirmAction(null);
  };

  const handleOpenModal = () => {
    setFeedback(null);
    setTimeErr(false);
    setCreateForm(initialLeaveState);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCreateFormChange = (field) => (event) => {
    setCreateForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCreateLeave = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setTimeErr(false);
    setFeedback(null);

    const {
      selectedDate,
      startTime,
      endTime,
      selectedMechanic,
      reason,
      title,
      notes,
    } = createForm;

    const parseTime = (timeValue) => {
      const [hours, minutes] = timeValue.split(":").map(Number);
      return { hours, minutes };
    };

    const { hours: startHours, minutes: startMinutes } = parseTime(startTime);
    const { hours: endHours, minutes: endMinutes } = parseTime(endTime);

    const invalidTime =
      Number.isNaN(startHours) ||
      Number.isNaN(startMinutes) ||
      Number.isNaN(endHours) ||
      Number.isNaN(endMinutes) ||
      startTime.length !== 5 ||
      endTime.length !== 5 ||
      startHours > 23 ||
      startMinutes > 59 ||
      endHours > 23 ||
      endMinutes > 59 ||
      startTime[2] !== ":" ||
      endTime[2] !== ":" ||
      startHours > endHours ||
      (startHours === endHours && startMinutes >= endMinutes);

    if (invalidTime) {
      setTimeErr(true);
      setIsSubmitting(false);
      return;
    }

    // Convert local time to UTC for backend storage
    // User enters times in their local timezone, we convert to UTC
    let startDateTime, endDateTime;
    try {
      const localStartDateTime = new Date(`${selectedDate}T${startTime}:00`);
      const localEndDateTime = new Date(`${selectedDate}T${endTime}:00`);

      // Validate that dates were parsed correctly
      if (isNaN(localStartDateTime.getTime()) || isNaN(localEndDateTime.getTime())) {
        setFeedback({ message: 'Invalid date or time. Please check your inputs and try again.', severity: 'error' });
        setIsSubmitting(false);
        return;
      }

      // UTC datetime strings for backend (YYYY-MM-DD HH:MM:SS format)
      startDateTime = localStartDateTime.toISOString().slice(0, 19).replace('T', ' ');
      endDateTime = localEndDateTime.toISOString().slice(0, 19).replace('T', ' ');
    } catch (error) {
      console.error('[LeaveRequest] Date parsing error:', error);
      setFeedback({ message: 'Invalid date or time. Please check your inputs and try again.', severity: 'error' });
      setIsSubmitting(false);
      return;
    }

    const selectedMechanicObject = mechanics.find(
      (mechanic) => mechanic.MechanicFirstName === selectedMechanic
    );
    const mechanic_id = selectedMechanicObject?.MechanicId;
    const scId = localStorage.getItem("SC_Id");

    const leavePayload = {
      leave_date: selectedDate,
      start_date_time: startDateTime,
      end_date_time: endDateTime,
      title,
      notes,
      leave_reason: reason,
      status: "pending",
    };

    try {
      const response = await fetch(
        `${envVariable.serverId}/SCO/VacationRequest.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: "create",
            leavePayload: leavePayload,
            mechanic_id,
            sc_id: scId,
          }),
        }
      );

      const result = await response.json();

      if (result.status === "Success") {
        setFeedback({ message: result.message, severity: "success" });
        fetchLeaves();
        setCreateForm(initialLeaveState);
      } else {
        setFeedback({
          message: result.message || "Unable to create leave",
          severity: "error",
        });
      }
    } catch (submitError) {
      console.error("Error submitting leave request:", submitError);
      setFeedback({
        message: "Unexpected error occurred. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <MenuBar />
      <PageTabs
        tabs={pageTabs}
        currentTab={statusFilter}
        onChange={handleStatusFilterChange}
        ariaLabel="Leave status tabs"
      />
      <CollapsiblePanel id="leave-toolbar" title="Leave request controls" className="sco-toolbar-collapsible">
        <div className="tasks-search-section sco-toolbar">
          <div className="tasks-search-inputs">
            <div className="page-input-block">
              <label className="task-label">Mechanic&apos;s Name</label>
              <input
                type="text"
                placeholder="Search by mechanic name"
                className="search-input"
                value={searchTerm}
                onChange={(event) => handleSearchChange(event.target.value)}
              />
            </div>
          </div>
          <div className="sco-toolbar-actions">
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenModal}
            >
              Create A Leave
            </Button>
          </div>
        </div>
      </CollapsiblePanel>

      <div className="tasks-container">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <table className="tasks-table">
          <thead>
            <tr>
              <th
                className="sortable-header"
                onClick={() => handleSortFieldChange("leaveId")}
              >
                Leave ID {sortField === "leaveId" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th
                className="sortable-header"
                onClick={() => handleSortFieldChange("mechanicName")}
              >
                Mechanic Name {sortField === "mechanicName" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Title</th>
              <th
                className="sortable-header"
                onClick={() => handleSortFieldChange("leaveDate")}
              >
                Leave Date {sortField === "leaveDate" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="table-empty-state">
                  Loading leaves...
                </td>
              </tr>
            ) : visibleLeaves.length > 0 ? (
              visibleLeaves.map((leave) => {
                const acceptKey = `${leave.id}-approved`;
                const rejectKey = `${leave.id}-rejected`;

                return (
                  <tr key={leave.id}>
                    <td>{leave.leaveId}</td>
                    <td>{leave.mechanicName}</td>
                    <td>{leave.title}</td>
                    <td>{leave.leaveDate}</td>
                    <td>{leave.startTime}</td>
                    <td>{leave.endTime}</td>
                    <td>{formatStatus(leave.status)}</td>
                    <td>
                      <button
                        className="leave-action-btn accept"
                        onClick={() => handleActionRequest(leave, "approved")}
                        disabled={
                          actionInProgress === acceptKey ||
                          leave.status === "approved"
                        }
                      >
                        {actionInProgress === acceptKey ? (
                          <CircularProgress size={16} />
                        ) : (
                          "Accept"
                        )}
                      </button>
                      <button
                        className="leave-action-btn reject"
                        onClick={() => handleActionRequest(leave, "rejected")}
                        disabled={
                          actionInProgress === rejectKey ||
                          leave.status === "rejected"
                        }
                      >
                        {actionInProgress === rejectKey ? (
                          <CircularProgress size={16} />
                        ) : (
                          "Reject"
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} className="table-empty-state">
                  No leaves found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {filteredLeaves.length > 0 && (
          <div className="pagination pagination-compact">
            <button
              className="page-item"
              onClick={handlePaginationLeft}
              disabled={currentPage === 1}
            >
              {"<"}
            </button>
            <span className="pagination-status">
              Page {currentPage} / {totalPages}
            </span>
            <button
              className="page-item"
              onClick={handlePaginationRight}
              disabled={currentPage >= totalPages}
            >
              {">"}
            </button>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="leave-modal-card">
            <div className="leave-modal-header">
              <Typography variant="h6">Create A Leave</Typography>
              <button className="modal-close-button" onClick={handleCloseModal}>
                &times;
              </button>
            </div>
            <form onSubmit={handleCreateLeave} className="leave-form">
              <TextField
                label="Title"
                value={createForm.title}
                onChange={handleCreateFormChange("title")}
                required
                fullWidth
              />
              <TextField
                label="Select Date"
                type="date"
                value={createForm.selectedDate}
                onChange={handleCreateFormChange("selectedDate")}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
              <TextField
                label="Start Time"
                type="time"
                value={createForm.startTime}
                onChange={handleCreateFormChange("startTime")}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
              <TextField
                label="End Time"
                type="time"
                value={createForm.endTime}
                onChange={handleCreateFormChange("endTime")}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
              {timeErr && (
                <Typography color="error">
                  Invalid time. Ensure start time is earlier than end time.
                </Typography>
              )}
              <TextField
                label="Notes"
                value={createForm.notes}
                onChange={handleCreateFormChange("notes")}
                multiline
                minRows={3}
                fullWidth
              />
              <FormControl fullWidth required>
                <InputLabel id="mechanic-label">Select Mechanic</InputLabel>
                <Select
                  labelId="mechanic-label"
                  label="Select Mechanic"
                  value={createForm.selectedMechanic}
                  onChange={handleCreateFormChange("selectedMechanic")}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {mechanics.map((mechanic) => (
                    <MenuItem
                      key={mechanic.MechanicId}
                      value={mechanic.MechanicFirstName}
                    >
                      {mechanic.MechanicFirstName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel id="reason-label">Reason</InputLabel>
                <Select
                  labelId="reason-label"
                  label="Reason"
                  value={createForm.reason}
                  onChange={handleCreateFormChange("reason")}
                >
                  <MenuItem value="sick">Sick</MenuItem>
                  <MenuItem value="vacation">Vacation</MenuItem>
                  <MenuItem value="lunch">Lunch</MenuItem>
                </Select>
              </FormControl>
              {feedback?.message && (
                <Alert severity={feedback.severity}>{feedback.message}</Alert>
              )}
              <Box className="leave-modal-actions">
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <CircularProgress size={20} /> : "Submit"}
                </Button>
              </Box>
            </form>
          </div>
        </div>
      )}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="leave-modal-card">
            <Typography variant="h6" sx={{ mb: 2 }}>
              Are you sure you want to {confirmAction.nextStatus === "approved" ? "approve" : "reject"} this leave?
            </Typography>
            <Typography sx={{ mb: 3 }}>
              This action will update the leave status for {confirmAction.leave.mechanicName}.
            </Typography>
            <Box className="leave-modal-actions">
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleConfirmAction(true)}
              >
                Yes
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => handleConfirmAction(false)}
              >
                Cancel
              </Button>
            </Box>
          </div>
        </div>
      )}
      {taskConflictDialog && (
        <div className="modal-overlay">
          <div className="leave-modal-card">
            <Typography variant="h6" sx={{ mb: 2, color: '#d32f2f' }}>
              Task Conflicts Detected
            </Typography>
            <Typography sx={{ mb: 2 }}>
              Approving this leave will conflict with the following assigned tasks:
            </Typography>
            <Box sx={{
              maxHeight: '300px',
              overflow: 'auto',
              mb: 3,
              border: '1px solid #ddd',
              borderRadius: '4px',
              p: 2,
              backgroundColor: '#f9f9f9'
            }}>
              {taskConflictDialog.conflictingTasks.map((task, index) => (
                <Box key={task.task_id} sx={{ mb: 2, pb: 2, borderBottom: index < taskConflictDialog.conflictingTasks.length - 1 ? '1px solid #ddd' : 'none' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Task #{task.task_id}: {task.service_info}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Start: {new Date(task.calendar_start + " UTC").toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    End: {new Date(task.calendar_end + " UTC").toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography sx={{ mb: 3, fontWeight: 'bold' }}>
              Do you want to unassign these tasks and approve the leave?
            </Typography>
            <Box className="leave-modal-actions">
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  updateLeaveStatus(taskConflictDialog.leave, taskConflictDialog.nextStatus, true);
                  setTaskConflictDialog(null);
                }}
              >
                Confirm & Unassign Tasks
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setTaskConflictDialog(null)}
              >
                Cancel
              </Button>
            </Box>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
};

export default LeaveRequest;
